import React, { useEffect, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';

// Material Resources
import {
  IconButton,
  Input,
  Button,
  Tooltip,
  Typography,
  TableCell,
} from '@material-ui/core';
import AssignmentReturnedIcon from '@material-ui/icons/AssignmentReturned';
import RefreshIcon from '@material-ui/icons/Refresh';

// GraphQl
import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';

// Components & Others
import LoadingIndicator from '../../../LoadingIndicator';
import {
  LIST_PARTICIPANT_RAISING_VALIDATED,
  GET_REPORT_EXCEL,
  FILE_DOWNLOAD,
} from '../queries';
import {
  generateColumn,
  edgeToList,
  decimalAdjust,
  errorMessage,
  successMessage,
  downloadBase64Document,
} from '../../../../utils/commonFunctions';
import NumberFormat from 'react-number-format';
import { textLabels } from '../../../../translations/components/MUIDataTable';
import {
  POSITION_CHOICES,
  COMPANY_CHOICES,
  LINKED_TYPES,
} from '../../../Cognito/StepThreeForm/LinkedPeople/constants';
import CustomOptions from 'src/components/CustomOptions';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import PDFViewerModal from '../pdfViewer';

// styles and icons
import useStyles from './styles';

function CustomToolbar() {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [getReport] = useLazyQuery(GET_REPORT_EXCEL, {
    fetchPolicy: 'no-cache',
    enqueueSnackbar,
    onError: error => errorMessage(error, enqueueSnackbar),
    onCompleted: pdfResponse => {
      if (pdfResponse.vinculatedExcel) {
        if (!pdfResponse.vinculatedExcel.file)
          errorMessage('Error, intente de nuevo.', enqueueSnackbar);
        if (pdfResponse.vinculatedExcel.file) {
          downloadBase64Document(
            pdfResponse.vinculatedExcel.file,
            'vinculados.xlsx',
            'xlsx',
          );
          successMessage('Éxito, descarga completada.', enqueueSnackbar);
        }
      } else errorMessage('Error, intente de nuevo.', enqueueSnackbar);
    },
  });

  const handleReport = () => {
    getReport();
  };

  return (
    <Tooltip title="Descargar reporte">
      <IconButton color="secondary" onClick={handleReport}>
        <AssignmentReturnedIcon />
      </IconButton>
    </Tooltip>
  );
}

function VinculatedValidatedList() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [participationList, setParticipationList] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [pageInfo, setPageInfo] = useState('');
  const [totalCount, setTotalCount] = useState('');
  const [listLength, setListLength] = useState(0);
  const [pageLength, setPageLength] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchTextInput, setSearchTextInput] = useState('');
  const [urls, setUrls] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const { loading, error, data, fetchMore, refetch } = useQuery(
    LIST_PARTICIPANT_RAISING_VALIDATED,
    {
      fetchPolicy: 'no-cache',
      variables: {
        search: searchText ? searchText : null,
        first: 10,
        afterCursor: null,
        last: null,
        beforeCursor: null,
      },
    },
  );

  if (error) {
    console.error('list clients', error);
  }

  useEffect(() => {
    if (!!data) {
      const participationListTmp = edgeToList(
        data,
        'listValidatedParticipantRaising',
      )
        .filter(({ lastParticipation }) => !!lastParticipation)
        .map(participation => {
          const {
            id,
            lastParticipation,
            createdDate,
            dateApproved,
            numFee,
            amount,
            client,
            raising,
            classFund,
            validated,
            hasSignedSd,
          } = participation;
          setCurrencySymbol(raising?.fund?.currency?.symbol);
          const newParticipation = {
            options: {
              id: id,
              state: lastParticipation.state,
              validated: validated,
              participantID: client.id,
              hasSignedSd: hasSignedSd,
            },
            fund: raising?.fund?.businessName,
            order: raising?.acronym,
            clientID: client.id,
            state: lastParticipation.state,
            dateEntry: createdDate,
            dateApproved: dateApproved,
            quotes: numFee,
            validated: validated,
            classFund: classFund ? classFund.name : '',
            amount: decimalAdjust('ceil', amount, -2),
            participants: [],
            percentajes: [],
            linkedPeoples: [],
          };
          newParticipation.participants.push(client.user);
          newParticipation.percentajes.push(
            lastParticipation.participationPercentage,
          );
          edgeToList(participation, 'linkedPeople').forEach(item => {
            newParticipation.linkedPeoples.push({
              name:
                item.linkedPeople.firstName + ' ' + item.linkedPeople.lastName,
              document: item.linkedPeople.documentNumber,
              company: COMPANY_CHOICES.find(
                element => element.value == item?.linkedPeople.company,
              )?.label,
              position: POSITION_CHOICES.find(
                element => element.value == item?.linkedPeople.position,
              )?.label,
              linkedType: LINKED_TYPES.find(
                element => element.value == item?.linkedType,
              )?.label,
            });
          });
          edgeToList(lastParticipation, 'coParticipants').forEach(
            ({ client: subClient, participationPercentage }) => {
              if (subClient?.linkedpeopleclientrelationSet?.edges) {
                subClient.linkedpeopleclientrelationSet.edges.forEach(item => {
                  newParticipation.linkedPeoples.push({
                    name:
                      item.node.linkedPeople.firstName +
                      ' ' +
                      item.node.linkedPeople.lastName,
                    document: item.node.linkedPeople.documentNumber,
                    company: COMPANY_CHOICES.find(
                      element =>
                        element.value == item?.node.linkedPeople.company,
                    )?.label,
                    position: POSITION_CHOICES.find(
                      element =>
                        element.value == item?.node.linkedPeople.position,
                    )?.label,
                    linkedType: LINKED_TYPES.find(
                      element => element.value == item?.node.linkedType,
                    )?.label,
                  });
                });
              }
              newParticipation.participants.push(subClient.user);
              newParticipation.percentajes.push(participationPercentage);
            },
          );
          return newParticipation;
        });
      setParticipationList(participationListTmp);
      setListLength(participationListTmp.length);
      setTotalCount(data.listValidatedParticipantRaising.totalCount);
      setPageInfo(data.listValidatedParticipantRaising.pageInfo);
    }
  }, [data]);

  const [downloadFile, { loadingFile, dataFile }] = useMutation(FILE_DOWNLOAD, {
    onError(error) {
      errorMessage(error, enqueueSnackbar);
    },
    onCompleted(data) {
      if (data?.fileDownloadSd?.urls?.length < 1) {
        errorMessage('No existen archivos', enqueueSnackbar);
      } else {
        setUrls(data?.fileDownloadSd?.urls);
        setShowPreview(true);
        successMessage('Archivos descargados.', enqueueSnackbar);
      }
    },
  });

  const columnOptions = {
    filter: false,
    sort: true,
  };

  const columns = [
    generateColumn('Fondo', 'fund', {
      customBodyRender: value => <span>{value}</span>,
    }),
    generateColumn('Levantamiento', 'order', {
      customBodyRender: value => <span>{value}</span>,
    }),
    generateColumn('Clase', 'classFund'),
    generateColumn('Participante', 'participants', {
      ...columnOptions,
      customBodyRender: participants => (
        <div className={classes.multilineCell}>
          {participants.map(
            ({ firstName, lastName, maternalSurname, isVinculated }) =>
              isVinculated ? (
                <span>
                  <b>{`${firstName} ${lastName} ${maternalSurname}`}</b> |{' '}
                </span>
              ) : (
                <span>{`${firstName} ${lastName} ${maternalSurname}`}</span>
              ),
          )}
        </div>
      ),
    }),
    generateColumn('Cuotas', 'quotes'),
    generateColumn('Monto', 'amount', {
      ...columnOptions,
      customBodyRender: amount => (
        <>
          {`${currencySymbol} `}
          <NumberFormat
            displayType="text"
            fixedDecimalScale
            decimalScale={2}
            thousandSeparator
            value={amount}
          />
        </>
      ),
    }),
    generateColumn('Fecha Creación', 'dateEntry', {
      ...columnOptions,
      customBodyRender: value => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    }),
    generateColumn('Porcentaje', 'percentajes', {
      ...columnOptions,
      customBodyRender: percentajes => (
        <div className={classes.multilineCell}>
          {percentajes.map((percentaje, i) =>
            i > 0 ? (
              <span> | {percentaje}% |</span>
            ) : (
              <span>{percentaje}%</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Vinculado', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ name }) =>
            name ? (
              <span>
                {` ${name}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Vinculación', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ linkedType }) =>
            linkedType ? (
              <span>
                {` ${linkedType}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Documento', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ document }) =>
            document ? (
              <span>
                {` ${document}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Empresa', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ company }) =>
            company ? (
              <span>
                {` ${company}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Cargo', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ position }) =>
            position ? (
              <span>
                {` ${position}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Fecha Aprobación', 'dateApproved', {
      ...columnOptions,
      customBodyRender: value =>
        value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '',
    }),
    generateColumn('Opciones', 'options', {
      ...columnOptions,
      customBodyRender: value => {
        const { id, hasSignedSd } = value;
        let options = [];
        if (hasSignedSd) {
          options.push('ocustom');
        }
        return (
          <CustomOptions
            id={id}
            model="Orden"
            options={options}
            customOption={
              <>
                <Tooltip title="Descargar DJ" placement="top" arrow>
                  <IconButton
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={$ev => {
                      $ev.stopPropagation();
                      downloadFile({
                        variables: { orderId: id },
                      });
                    }}
                    className={classes.optionButtons}
                  >
                    <CloudDownloadIcon className={classes.optionIcon} />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        );
      },
      customHeadRender: columnMeta => {
        return (
          <TableCell
            id={columnMeta.index}
            key={columnMeta.index}
            style={{ borderBottom: 'none' }}
          >
            <Typography
              variant="subtitle1"
              align="left"
              className={classes.tableTitle}
            >
              {columnMeta.label}
            </Typography>
          </TableCell>
        );
      },
    }),
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    selectableRows: 'none',
    print: false,
    filter: true,
    download: false,
    pagination: false,
    textLabels,
    customToolbar: () => {
      return (
        <>
          <CustomToolbar />
          <Tooltip title="Refrescar">
            <IconButton
              color="secondary"
              onClick={e => {
                setPageLength(10);
                setSearchText('');
                refetch();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    },
    customSearchRender: () => {
      return (
        <div>
          <Input
            autoFocus
            className={classes.searchInput}
            variant="outlined"
            type="text"
            onChange={e => {
              setSearchTextInput(e.target.value);
            }}
          ></Input>
          <Button
            variant="contained"
            className={classes.searchButton}
            onClick={() => {
              setSearchText(searchTextInput);
            }}
          >
            {' '}
            Buscar{' '}
          </Button>
        </div>
      );
    },
    onSearchClose: () => {
      setPageLength(10);
      setSearchText('');
      refetch();
    },
  };

  return loading ? (
    <LoadingIndicator />
  ) : (
    <>
      <MUIDataTable
        title="Vinculados Aprobados"
        data={participationList}
        columns={columns}
        options={options}
      />
      <PDFViewerModal
        showModal={showPreview}
        onClosePress={() => setShowPreview(false)}
        fileURLs={urls}
        loading={loading}
      />
      <div className={classes.buttonContainer}>
        {listLength < pageLength && pageLength > totalCount ? (
          <div className={classes.paginationText}>
            {totalCount} de {totalCount}
          </div>
        ) : (
          <div className={classes.paginationText}>
            {pageLength} de {totalCount}
          </div>
        )}
        {pageLength - listLength > 0 && listLength != totalCount ? (
          <div
            className={classes.paginationButton}
            onClick={() => {
              if (pageLength > totalCount) setPageLength(pageLength - 10);
              else setPageLength(pageLength - listLength);
              if (pageInfo?.startCursor) {
                fetchMore({
                  variables: {
                    beforeCursor: pageInfo.startCursor,
                    last: 10,
                    afterCursor: null,
                    first: null,
                  },
                  updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return fetchMoreResult;
                  },
                });
              }
            }}
          >
            {' '}
            Anterior{' '}
          </div>
        ) : (
          <span></span>
        )}
        {pageLength <= totalCount ? (
          <div
            className={classes.paginationButton}
            onClick={() => {
              setPageLength(pageLength + listLength);
              fetchMore({
                variables: {
                  afterCursor: pageInfo?.endCursor,
                  first: 10,
                  last: null,
                  beforeCursor: null,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return fetchMoreResult;
                },
              });
            }}
          >
            {' '}
            Siguiente{' '}
          </div>
        ) : (
          <span></span>
        )}
      </div>
    </>
  );
}

export default VinculatedValidatedList;
